import "./styles.css";
import React, { useMemo } from "react";
import {
  FaUserAlt,
  FaIdCard,
  FaCheckCircle,
  FaExclamationCircle,
  FaClock,
  FaMoneyBill,
  FaTruck,
} from "react-icons/fa";
import { IoMdCheckboxOutline } from "react-icons/io";
import { PiCityBold } from "react-icons/pi";
import { TbDeviceIpadExclamation } from "react-icons/tb";
import { useNavigate } from "react-router-dom";
import { format, toZonedTime } from "date-fns-tz";
import { addHours } from "date-fns";

const DISPLAY_TIME_ZONE = "America/Sao_Paulo";

const getFirstMovDate = (movimentos) => {
  if (!movimentos || movimentos.length === 0) return null;
  let min = null;

  for (const movimento of movimentos) {
    if (!movimento?.datahora) continue;
    const data = new Date(movimento.datahora);
    if (Number.isNaN(data.getTime())) continue;
    if (min === null || data < min) min = data;
  }

  return min;
};

const getFirstArrivalDate = (movimentos) => {
  return getFirstMovDate(
    movimentos?.filter((movimento) => movimento?.tipo === 1) || []
  );
};

const getLastMovDate = (movimentos) => {
  if (!movimentos || movimentos.length === 0) return null;
  let max = null;

  for (const movimento of movimentos) {
    if (!movimento?.datahora) continue;
    const data = new Date(movimento.datahora);
    if (Number.isNaN(data.getTime())) continue;
    if (max === null || data > max) max = data;
  }

  return max;
};

const formatTravelTime = (start, end) => {
  if (!start || !end) return "-";

  const diffMs = end.getTime() - start.getTime();

  if (!Number.isFinite(diffMs) || diffMs < 0) return "-";

  const totalMinutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return `${String(hours).padStart(2, "0")}h ${String(minutes).padStart(
    2,
    "0"
  )}m`;
};

const getTravelDayTransition = (start, end) => {
  if (!start || !end || end.getTime() < start.getTime()) {
    return {
      isDayChange: false,
      previousDate: null,
      currentDate: null,
    };
  }

  const previousZonedDate = toZonedTime(start, DISPLAY_TIME_ZONE);
  const currentZonedDate = toZonedTime(end, DISPLAY_TIME_ZONE);
  const previousDay = format(previousZonedDate, "yyyy-MM-dd");
  const currentDay = format(currentZonedDate, "yyyy-MM-dd");

  return {
    isDayChange: previousDay !== currentDay,
    previousDate: format(previousZonedDate, "dd/MM/yyyy"),
    currentDate: format(currentZonedDate, "dd/MM/yyyy"),
  };
};

const sortVisitsByFirstMovement = (visitas) => {
  return [...(Array.isArray(visitas) ? visitas : [])]
    .map((visita, index) => ({ ...visita, originalIndex: index }))
    .sort((firstVisit, secondVisit) => {
      const firstDate = getFirstMovDate(firstVisit.movimentos);
      const secondDate = getFirstMovDate(secondVisit.movimentos);

      if (firstDate && secondDate) {
        return firstDate.getTime() - secondDate.getTime();
      }

      if (firstDate && !secondDate) return -1;
      if (!firstDate && secondDate) return 1;
      return firstVisit.originalIndex - secondVisit.originalIndex;
    });
};

const ListaVisita = ({
  listaVisitas = [],
  listaVisitasReferencia,
  idmotorista,
  maxHeight,
}) => {
  const navigate = useNavigate();

  const formatarDataHora = (datahora) => {
    if (!datahora) return "-";
    const dataZoned = toZonedTime(datahora, DISPLAY_TIME_ZONE);
    const dataSomada = addHours(dataZoned, 0);
    const formato = "dd/MM/yyyy HH:mm:ss";
    return format(dataSomada, formato);
  };

  const getStatusMeta = (situacaovisita) => {
    switch (situacaovisita) {
      case 0:
        return { className: "status-nao-visitado", label: "Nao visitado", iconColor: "#64748b" };
      case 1:
        return { className: "status-visitado", label: "Visitado", iconColor: "#16a34a" };
      case 2:
        return { className: "status-nao-entregue", label: "Nao entregue", iconColor: "#dc2626" };
      case 3:
        return { className: "status-em-andamento", label: "Em andamento", iconColor: "#ca8a04" };
      default:
        return { className: "status-nao-visitado", label: "Nao visitado", iconColor: "#64748b" };
    }
  };

  const sortedVisitas = useMemo(() => {
    return sortVisitsByFirstMovement(listaVisitas);
  }, [listaVisitas]);

  const sortedReferenceVisits = useMemo(() => {
    const referenceVisits = Array.isArray(listaVisitasReferencia)
      ? listaVisitasReferencia
      : listaVisitas;

    return sortVisitsByFirstMovement(referenceVisits);
  }, [listaVisitas, listaVisitasReferencia]);

  const travelDetails = useMemo(() => {
    return sortedReferenceVisits.reduce((times, visita, index) => {
      const previousVisit = sortedReferenceVisits[index - 1];
      const previousLastMovement = getLastMovDate(previousVisit?.movimentos);
      const currentArrival = getFirstArrivalDate(visita.movimentos);
      const dayTransition = getTravelDayTransition(
        previousLastMovement,
        currentArrival
      );

      times[visita.IDvisita] = {
        duration: formatTravelTime(previousLastMovement, currentArrival),
        previousVisitId: previousVisit?.IDvisita || null,
        ...dayTransition,
      };

      return times;
    }, {});
  }, [sortedReferenceVisits]);

  const handleCardClick = (IDmotorista, IDvisita) => {
    navigate(`/Visita/${IDmotorista}/${IDvisita}`);
  };

  return (
    <div
      className="card-container visita-card-container"
      style={{ maxHeight: maxHeight || "58vh", overflowY: "auto", padding: "10px" }}
    >
      {sortedVisitas.map((visita, index) => {
        const statusMeta = getStatusMeta(visita.situacaovisita);
        const travel = travelDetails[visita.IDvisita] || { duration: "-" };
        const nextVisit = sortedVisitas[index + 1];
        const nextTravel = nextVisit
          ? travelDetails[nextVisit.IDvisita]
          : null;
        const showTransition =
          nextTravel?.previousVisitId === visita.IDvisita &&
          (nextTravel.duration !== "-" || nextTravel.isDayChange);

        return (
          <React.Fragment key={visita.IDvisita}>
            <div
              className={`card visita-card ${statusMeta.className}`}
              onClick={() => handleCardClick(idmotorista, visita.IDvisita)}
            >
              <div className="visita-status-chip">{statusMeta.label}</div>

              <div className="card-content">
                <div className="visita-card-header">
                  <h3 className="visita-card-title">
                    <FaUserAlt style={{ marginRight: "8px" }} />
                    Cliente: {visita.IDcliente} - {visita.nomecliente}
                  </h3>
                  <div className="visita-card-meta">
                    <span className="visita-card-subtitle">Visita n: {visita.IDvisita}</span>
                    {visita.situacaovisita === 1 && (
                      <FaCheckCircle style={{ color: statusMeta.iconColor }} />
                    )}
                  </div>
                </div>

                <div className="visita-card-body">
                  <div className="visita-info-column">
                    <div className="visita-info-item">
                      <FaIdCard />
                      <span>{visita.enderecocliente}</span>
                    </div>
                    <div className="visita-info-item">
                      <PiCityBold />
                      <span>{visita.cidadecliente}</span>
                    </div>
                    <div className="visita-info-item visita-info-inline">
                      <span className="visita-info-pair">
                        <FaMoneyBill />
                        <strong>R$ {visita.valor}</strong>
                      </span>
                      <span className="visita-info-pair">
                        <FaTruck />
                        <span>Volume total da carga: {visita.quantidade ?? 0}</span>
                      </span>
                    </div>
                    {visita.situacaovisita === 1 && (
                      <div className="visita-info-item visita-time-spent">
                        <FaClock />
                        <b>Tempo Gasto: {visita.tempo} min.</b>
                      </div>
                    )}
                    <div className="visita-info-item visita-travel-time">
                      <FaTruck />
                      <b>Tempo de deslocamento: {travel.duration}</b>
                    </div>
                    <div className="visita-info-item">
                      <TbDeviceIpadExclamation />
                      <span>Obs: {visita.observacaovisita ? visita.observacaovisita : "-"}</span>
                    </div>
                  </div>

                  <div className="visita-movimentos-column">
                    {(() => {
                      const mov1 = visita.movimentos?.find((m) => m.tipo === 1);
                      const mov2 = visita.movimentos?.find((m) => m.tipo === 2);
                      const mov3 = visita.movimentos?.find((m) => m.tipo === 3);

                      const calculateDiffMs = (start, end) => {
                        if (!start || !end) return null;
                        const startTime = new Date(start).getTime();
                        const endTime = new Date(end).getTime();

                        if (
                          !Number.isFinite(startTime) ||
                          !Number.isFinite(endTime) ||
                          endTime < startTime
                        ) {
                          return null;
                        }

                        return endTime - startTime;
                      };

                      const formatElapsedTime = (diffMs) => {
                        if (diffMs === null) return "-";
                        const horas = Math.floor(diffMs / (1000 * 60 * 60));
                        const minutos = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                        return `${String(horas).padStart(2, "0")}h ${String(minutos).padStart(2, "0")}m`;
                      };

                      const deliveryTimeMs = calculateDiffMs(
                        mov2?.datahora,
                        mov3?.datahora
                      );
                      const totalTimeMs = calculateDiffMs(
                        mov1?.datahora,
                        mov3?.datahora
                      );
                      const waitingTimeMs =
                        totalTimeMs !== null &&
                        deliveryTimeMs !== null &&
                        totalTimeMs >= deliveryTimeMs
                          ? totalTimeMs - deliveryTimeMs
                          : null;
                      const tempoEntrega = formatElapsedTime(deliveryTimeMs);
                      const tempoTotal = formatElapsedTime(totalTimeMs);
                      const tempoEspera = formatElapsedTime(waitingTimeMs);

                      return (
                        <>
                          {visita.movimentos?.map((mov) => (
                            <div
                              className="visita-movimento-row"
                              key={
                                mov.IDmovimento ||
                                `${visita.IDvisita}-${mov.tipo}-${mov.datahora}`
                              }
                            >
                              <span className="visita-movimento-label">
                                {(() => {
                                  switch (mov.tipo) {
                                    case 1:
                                      return (
                                        <>
                                          <IoMdCheckboxOutline className="visita-movimento-icon" />
                                          Chegou no local.
                                        </>
                                      );
                                    case 2:
                                      return (
                                        <>
                                          <IoMdCheckboxOutline className="visita-movimento-icon" />
                                          Comecou entrega.
                                        </>
                                      );
                                    case 3:
                                      return (
                                        <>
                                          <IoMdCheckboxOutline className="visita-movimento-icon" />
                                          {/* Colheu assinatura. */}
                                          Comprovou entrega.
                                        </>
                                      );
                                    case 4:
                                      return (
                                        <>
                                          <IoMdCheckboxOutline className="visita-movimento-icon" />
                                          Finalizou entrega.
                                        </>
                                      );
                                    case 5:
                                      return (
                                        <>
                                          <FaExclamationCircle className="visita-movimento-icon visita-movimento-alerta" />
                                          Ocorrencia.
                                        </>
                                      );
                                    default:
                                      return mov.tipo;
                                  }
                                })()}
                              </span>

                              <span className="visita-movimento-date">
                                <FaIdCard style={{ marginRight: "8px" }} />
                                {formatarDataHora(mov.datahora)}
                              </span>
                            </div>
                          ))}

                          <div className="visita-tempos">
                            {tempoEntrega !== "-" && (
                              <div className="visita-tempo-item">
                                <FaClock style={{ marginRight: "5px" }} />
                                Tempo de entrega: {tempoEntrega}
                              </div>
                            )}
                            {tempoTotal !== "-" && (
                              <div className="visita-tempo-item">
                                <FaClock style={{ marginRight: "5px" }} />
                                Tempo total: {tempoTotal}
                              </div>
                            )}
                            {tempoEspera !== "-" && (
                              <div className="visita-tempo-item visita-waiting-time">
                                <FaClock style={{ marginRight: "5px" }} />
                                Tempo de espera: {tempoEspera}
                              </div>
                            )}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>

            {showTransition ? (
              <div className="visita-transition" aria-label="Intervalo entre visitas">
                <span className="visita-transition-time">
                  <FaTruck /> Tempo de deslocamento: {nextTravel.duration}
                </span>
                {nextTravel.isDayChange ? (
                  <span className="visita-day-change">
                    Virada de dia: {nextTravel.previousDate} → {nextTravel.currentDate}
                  </span>
                ) : null}
              </div>
            ) : null}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default ListaVisita;
